"use client";

import * as React from "react";
import { formatInteger } from "@jasperlabs/number-format";

import { cn } from "../../lib/utils";

export type CircularProgressProps = Omit<
	React.ComponentProps<"div">,
	"children"
> & {
	/**
	 * Progress value as a percentage. Non-finite or missing values are treated as 0;
	 * negative values as 0; the ring fill is capped at 100%.
	 */
	value?: number | null;
	/**
	 * When true, the text label shows the real value (e.g. 108%) while the ring stays
	 * capped at 100%. Useful for over-converted / over-allocated scenarios.
	 */
	allowOverflow?: boolean;
	/**
	 * When set, labels above this rounded value render as `>{maxLabel}%` (e.g. `>999%`).
	 * The ring fill is unchanged. Compare against the rounded label so a boundary like
	 * 999.4% never flashes `1,000%`.
	 */
	maxLabel?: number;
	/**
	 * Visual treatment of the ring. `"danger"` paints the arc with `text-danger`;
	 * `"primary"` with `text-primary-400`; defaults to `text-success`.
	 */
	variant?: "default" | "danger" | "primary";
	/** Diameter of the ring in pixels. */
	size?: number;
	/** Stroke width of the ring in pixels. */
	strokeWidth?: number;
};

const RING_COLOR = {
	default: "text-success",
	danger: "text-danger",
	primary: "text-primary-400",
} as const satisfies Record<
	NonNullable<CircularProgressProps["variant"]>,
	string
>;

/**
 * Compact circular progress ring with a percentage label, for tight layouts such as
 * kanban cards. Pass `value` as a 0–100 percentage.
 */
function CircularProgress({
	className,
	value,
	allowOverflow = false,
	maxLabel,
	variant = "default",
	size = 16,
	strokeWidth = 2,
	...props
}: CircularProgressProps) {
	const numeric = Number(value);
	const safeValue = Number.isFinite(numeric) ? Math.max(0, numeric) : 0;
	const percent = Math.min(100, safeValue);
	const labelValue = Math.round(allowOverflow ? safeValue : percent);
	const isLabelCapped = maxLabel != null && labelValue > maxLabel;
	const visibleLabel = isLabelCapped
		? `>${formatInteger(maxLabel)}%`
		: `${formatInteger(labelValue)}%`;

	const radius = (size - strokeWidth) / 2;
	const circumference = 2 * Math.PI * radius;
	const offset = circumference - (percent / 100) * circumference;

	return (
		<div
			data-slot="circular-progress"
			role="progressbar"
			aria-valuemin={0}
			aria-valuemax={100}
			aria-valuenow={Math.round(percent)}
			aria-label={visibleLabel}
			className={cn("inline-flex items-center gap-1", className)}
			{...props}
		>
			<svg
				width={size}
				height={size}
				viewBox={`0 0 ${size} ${size}`}
				className="-rotate-90"
				aria-hidden
			>
				<circle
					cx={size / 2}
					cy={size / 2}
					r={radius}
					fill="none"
					stroke="#E6E8F0"
					strokeWidth={strokeWidth}
				/>
				<circle
					cx={size / 2}
					cy={size / 2}
					r={radius}
					fill="none"
					stroke="currentColor"
					strokeWidth={strokeWidth}
					strokeLinecap="round"
					strokeDasharray={circumference}
					strokeDashoffset={offset}
					className={cn(RING_COLOR[variant], "transition-all")}
				/>
			</svg>
			<span className="text-xs tabular-nums text-neutral-600">
				{visibleLabel}
			</span>
		</div>
	);
}

export { CircularProgress };
